import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Send, Pin, Trash2, Flame, Clock, Globe, Bell, BellOff, Hash, Users, Lock, Sparkles, UserPlus, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SuggestedConnections } from "@/components/SuggestedConnections";
import { Avatar, LevelBadge, timeAgo } from "@/components/common";
import { PageHeader } from "@/components/PageHeader";
import { Markdown } from "@/components/Markdown";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const FIRST_POST_PROMPTS = {
  "engineering-leadership": [
    "One engineering leadership lesson I learned the hard way:\n\n(What happened, and what I'd do differently now.)",
    "The hardest tradeoff I'm making on my team right now — speed vs. quality, hiring vs. focus, something else:",
    "A process or ritual we killed that made us faster. What did you cut?",
    "How do you keep senior engineers growing once there's no obvious 'next title'?",
  ],
  "ai-strategy": [
    "Where we are on our AI journey right now — experimenting, scaling, or stuck:\n\n(And the biggest open question I'm wrestling with.)",
    "One AI use case that actually moved a metric for us — and one that flopped:",
    "Build vs. buy vs. fine-tune: how are you deciding for your org?",
    "What's the most underrated risk in shipping AI features to real users?",
  ],
  "hiring-and-teams": [
    "The best interview question I ask — and what a great answer actually tells me:",
    "A hiring mistake I made and the signal I now watch for:",
    "How do you keep the bar high when you're under pressure to hire fast?",
    "Remote, hybrid, in-person — what's actually working for your team and why?",
  ],
  "founders": [
    "The hardest decision I'm weighing as a founder this quarter:\n\n(The guild has probably been here — would love a gut-check.)",
    "One thing I wish someone had told me before I started:",
    "How are you thinking about fundraising vs. bootstrapping right now?",
    "What's keeping you up at night — and what would 'solved' look like?",
  ],
  "career-growth": [
    "A piece of career advice I'd give my younger self, as specific as I can make it:",
    "The skill that unlocked my last level-up — and how I built it:",
    "How do you decide when it's time to leave vs. stay and grow?",
    "What does 'success' look like for you this year, beyond the title?",
  ],
};
const DEFAULT_FIRST_POST = [
  "Hi everyone — introducing myself.\n\nI lead ______ and I'm currently building ______. What brought me to Ikigora is ______.",
  "What's one thing you're figuring out at work right now that this community could help with?",
  "Share a win from this week — big or small. Let's celebrate each other.",
  "What's the best thing you read, watched, or learned recently? Drop a link.",
];

function pickPrompt(slug) {
  const list = (slug && FIRST_POST_PROMPTS[slug]) || DEFAULT_FIRST_POST;
  const day = Math.floor(Date.now() / 86400000);
  return list[day % list.length];
}
const INTRO_PROMPT = "Hi everyone — introducing myself.\n\nI lead ______ and I'm currently building ______. What brought me to Ikigora is ______.\n\nWould love to connect with others working on similar things.";

function Composer({ spaces, defaultSpace, onCreated, seed, onSeedUsed }) {
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [spaceId, setSpaceId] = useState(defaultSpace || "");
  const [showImg, setShowImg] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (seed) { setContent(seed); onSeedUsed && onSeedUsed(); }
  }, [seed]);

  const submit = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/posts", {
        content, image_url: imageUrl || null, space_id: spaceId || null,
      });
      setContent(""); setImageUrl(""); setShowImg(false);
      onCreated(res.data);
      toast.success("Posted · +10 pts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card rounded-xl p-5" data-testid="post-composer">
      <MarkdownEditor
        value={content}
        onChange={setContent}
        placeholder="Share a lesson, ask the guild, start a thread… (Markdown supported)"
        rows={3}
        testid="post-composer-input"
      />
      {(showImg || imageUrl) && (
        <div className="mt-3">
          <ImageUpload value={imageUrl} onChange={setImageUrl} aspect="aspect-[16/8]"
            label="Attach a photo" testid="post-image-upload" />
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <div className="flex items-center gap-3">
          {!defaultSpace && (
            <select
              value={spaceId}
              onChange={(e) => setSpaceId(e.target.value)}
              data-testid="post-composer-space-select"
              className="bg-transparent text-xs border border-border px-2 py-1.5 outline-none"
            >
              <option value="">General</option>
              {spaces.map((s) => (
                <option key={s.space_id} value={s.space_id}>{s.name}</option>
              ))}
            </select>
          )}
          <button onClick={() => setShowImg((v) => !v)} data-testid="post-composer-image-toggle"
            className={`text-sm inline-flex items-center gap-1.5 transition-colors ${showImg || imageUrl ? "text-gold" : "text-muted-foreground hover:text-foreground"}`}>
            Photo
          </button>
        </div>
        <button
          onClick={submit}
          disabled={busy || !content.trim()}
          data-testid="create-post-submit-button"
          className="inline-flex items-center gap-2 bg-gold text-accent-foreground rounded-md px-4 py-1.5 text-sm hover:bg-gold-hover transition-colors disabled:opacity-40"
        >
          <Send size={14} /> Post
        </button>
      </div>
    </div>
  );
}

function Comments({ postId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/posts/${postId}/comments`).then((r) => setComments(r.data));
  }, [postId]);

  const add = async () => {
    if (!text.trim()) return;
    try {
      const res = await api.post(`/posts/${postId}/comments`, { content: text });
      setComments((c) => [...c, res.data]);
      setText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not comment");
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border space-y-4" data-testid={`comments-${postId}`}>
      {comments.map((c) => (
        <div key={c.comment_id} className="flex gap-3">
          <Avatar src={c.author.picture} name={c.author.name} size={28} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{c.author.name}</span>
              <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{c.content}</p>
          </div>
        </div>
      ))}
      <div className="flex gap-3 items-center">
        <Avatar src={user?.picture} name={user?.name} size={28} />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a comment…"
          data-testid={`comment-input-${postId}`}
          className="flex-1 bg-transparent border-b border-border py-1.5 text-sm outline-none"
        />
        <button onClick={add} data-testid={`comment-submit-${postId}`}
          className="text-gold hover:text-gold-hover"><Send size={15} /></button>
      </div>
    </div>
  );
}

export function PostCard({ post, onChange, onDelete }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes_count);

  const like = async () => {
    try {
      const res = await api.post(`/posts/${post.post_id}/like`);
      setLiked(res.data.liked); setLikes(res.data.likes_count);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not like");
    }
  };

  const pin = async () => {
    const res = await api.post(`/admin/posts/${post.post_id}/pin`);
    onChange && onChange();
    toast.success(res.data.pinned ? "Pinned" : "Unpinned");
  };

  const del = async () => {
    await api.delete(`/admin/posts/${post.post_id}`);
    onDelete && onDelete(post.post_id);
    toast.success("Post removed");
  };

  return (
    <article className="border border-border bg-card rounded-xl p-5 fade-up" data-testid={`post-${post.post_id}`}>
      {post.pinned && (
        <div className="flex items-center gap-1.5 text-gold eyebrow mb-3">
          <Pin size={11} /> Pinned
        </div>
      )}
      <div className="flex items-start gap-3">
        <Link to={`/members/${post.author.user_id}`}>
          <Avatar src={post.author.picture} name={post.author.name} size={42} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link to={`/members/${post.author.user_id}`} className="text-sm font-semibold hover:text-gold transition-colors">
              {post.author.name}
            </Link>
            <LevelBadge level={post.author.level} />
            <span className="text-xs text-muted-foreground">· {timeAgo(post.created_at)}</span>
          </div>
          {post.author.title && <div className="text-xs text-muted-foreground">{post.author.title}</div>}
          {post.space && (
            <Link to={`/space/${post.space.slug}`}
              className="inline-block mt-2 text-[11px] font-mono uppercase tracking-wider border border-border px-2 py-0.5 hover:border-gold hover:text-gold transition-colors">
              #{post.space.name}
            </Link>
          )}
        </div>
        {user?.is_admin && (
          <div className="flex items-center gap-1">
            <button onClick={pin} data-testid={`pin-post-${post.post_id}`}
              className="p-1.5 text-muted-foreground hover:text-gold transition-colors"><Pin size={14} /></button>
            <button onClick={del} data-testid={`delete-post-${post.post_id}`}
              className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm sm:text-[15px] leading-relaxed">
        <Markdown>{post.content}</Markdown>
      </div>
      {post.image_url && (
        <img src={post.image_url} alt="" className="mt-4 w-full max-h-96 object-cover border border-border warm-img" />
      )}

      <div className="flex items-center gap-5 mt-4 pt-4 border-t border-border">
        <button onClick={like} data-testid={`like-post-button-${post.post_id}`}
          className={`inline-flex items-center gap-1.5 text-sm transition-colors ${liked ? "text-gold" : "text-muted-foreground hover:text-foreground"}`}>
          <Heart size={16} fill={liked ? "currentColor" : "none"} /> {likes}
        </button>
        <button onClick={() => setOpen((v) => !v)} data-testid={`comment-toggle-${post.post_id}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <MessageCircle size={16} /> {post.comment_count}
        </button>
      </div>
      {open && <Comments postId={post.post_id} />}
    </article>
  );
}

function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex items-center rounded-lg border border-border bg-secondary/40 p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            value === o.value ? "bg-card text-cobalt shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
          {o.icon && <o.icon size={13} />} {o.label}
        </button>
      ))}
    </div>
  );
}

export default function Feed() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("active");
  const [scope, setScope] = useState("all");
  const [subBusy, setSubBusy] = useState(false);
  const [seed, setSeed] = useState("");
  const [posted, setPosted] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => localStorage.getItem("intro_nudge_dismissed") === "1");

  const load = useCallback(async () => {
    setLoading(true);
    const sRes = await api.get("/spaces");
    setSpaces(sRes.data);
    const sp = slug ? sRes.data.find((s) => s.slug === slug) : null;
    setSpace(sp);
    const locked = sp && sp.premium && user?.plan !== "pro";
    if (locked) {
      setPosts([]);
      setLoading(false);
      return;
    }
    const params = slug ? { space: slug, sort } : { sort, scope };
    try {
      const pRes = await api.get("/posts", { params });
      setPosts(pRes.data);
    } catch (e) {
      setPosts([]);
    }
    setLoading(false);
  }, [slug, sort, scope, user]);

  useEffect(() => { load(); }, [load]);

  const defaultSpaceId = space?.space_id;
  const locked = space && space.premium && user?.plan !== "pro";

  const subscribe = async () => {
    if (!space) return;
    setSubBusy(true);
    try {
      const res = await api.post(`/spaces/${space.slug}/subscribe`);
      setSpace((s) => ({ ...s, subscribed: res.data.subscribed, subscriber_count: res.data.subscriber_count }));
      toast.success(res.data.subscribed ? `Subscribed to ${space.name}` : `Unsubscribed`);
    } catch (e) {
      toast.error("Could not update subscription");
    } finally {
      setSubBusy(false);
    }
  };

  const sortOptions = [
    { value: "active", label: "Most active", icon: Flame },
    { value: "latest", label: "Latest", icon: Clock },
  ];
  const scopeOptions = [
    { value: "all", label: "All", icon: Globe },
    { value: "subscribed", label: "Following", icon: Bell },
  ];

  const startPrompt = (text) => {
    setSeed(text);
    document.querySelector('[data-testid="post-composer-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const startFirstPost = () => startPrompt(pickPrompt(space?.slug));

  const dismissNudge = () => { localStorage.setItem("intro_nudge_dismissed", "1"); setNudgeDismissed(true); };
  const showIntroNudge = !space && user?.onboarded && !user?.has_posted && !posted && !nudgeDismissed;

  return (
    <div className="space-y-6">
      {space ? (
        <div className="border border-border bg-card rounded-xl p-6 sm:p-7 fade-up" data-testid="space-wall-header">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="eyebrow text-cobalt mb-1.5 flex items-center gap-1.5"><Hash size={12} /> Space
                {space.premium && <span className="inline-flex items-center gap-1 text-[10px] bg-cobalt text-white rounded-full px-2 py-0.5 not-italic"><Sparkles size={10} /> Pro</span>}
              </div>
              <h1 className="font-serif font-extrabold text-3xl sm:text-4xl tracking-tight">{space.name}</h1>
              {space.description && <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-xl">{space.description}</p>}
              <div className="mt-3 text-xs text-muted-foreground font-mono inline-flex items-center gap-1.5">
                <Users size={13} /> {space.subscriber_count ?? 0} subscriber{(space.subscriber_count ?? 0) === 1 ? "" : "s"}
              </div>
            </div>
            <button onClick={subscribe} disabled={subBusy} data-testid="space-subscribe-button"
              className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold transition-colors shrink-0 disabled:opacity-50 ${
                space.subscribed ? "border border-border text-foreground hover:bg-secondary/60" : "bg-cobalt text-white hover:bg-gold-hover"}`}>
              {space.subscribed ? <><BellOff size={15} /> Subscribed</> : <><Bell size={15} /> Subscribe</>}
            </button>
          </div>
        </div>
      ) : (
        <PageHeader eyebrow="The Wall" title="Community"
          subtitle="What the community is talking about right now." />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented options={sortOptions} value={sort} onChange={setSort} testid="feed-sort" />
        {!space && <Segmented options={scopeOptions} value={scope} onChange={setScope} testid="feed-scope" />}
      </div>

      {locked ? (
        <div className="text-center py-16 border border-cobalt/40 bg-secondary/40 rounded-xl" data-testid="space-locked">
          <Lock size={28} className="mx-auto text-cobalt mb-3" />
          <h2 className="font-serif text-2xl font-bold">This is a Pro-only space</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-5 max-w-sm mx-auto">Go Pro to join the conversation in premium spaces and unlock the rest of Ikigora.</p>
          <button onClick={() => navigate("/upgrade")} data-testid="space-upgrade-button"
            className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors">
            <Sparkles size={16} /> Go Pro
          </button>
        </div>
      ) : (
        <>
          {showIntroNudge && (
            <div className="relative border border-cobalt/40 bg-cobalt/5 rounded-xl p-5 sm:p-6 fade-up" data-testid="intro-nudge">
              <button onClick={dismissNudge} data-testid="intro-nudge-dismiss"
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors">
                <X size={16} />
              </button>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-cobalt/15 flex items-center justify-center shrink-0">
                  <UserPlus size={20} className="text-cobalt" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-serif text-xl font-bold">You haven't said hello yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-lg leading-relaxed">
                    The community would love to meet you. Take 30 seconds to introduce yourself — what you lead, what you're building, and what brought you here.
                  </p>
                  <button onClick={() => startPrompt(INTRO_PROMPT)} data-testid="intro-nudge-start"
                    className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors">
                    <Send size={14} /> Introduce yourself
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="border border-border bg-secondary/30 rounded-xl px-4 py-3 flex items-start gap-3 fade-up" data-testid="conversation-starter">
            <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
              <MessageCircle size={15} className="text-gold" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-gold text-[10px] mb-0.5">Conversation starter</div>
              <p className="text-sm text-foreground/90 leading-snug whitespace-pre-line line-clamp-2">{pickPrompt(space?.slug)}</p>
            </div>
            <button onClick={() => startPrompt(pickPrompt(space?.slug))} data-testid="use-starter-prompt"
              className="shrink-0 self-center inline-flex items-center gap-1.5 text-xs font-semibold border border-gold text-gold rounded-md px-3 py-1.5 hover:bg-gold hover:text-accent-foreground transition-colors">
              Use this
            </button>
          </div>
          <Composer spaces={spaces} defaultSpace={defaultSpaceId} seed={seed} onSeedUsed={() => setSeed("")} onCreated={(p) => { setPosts((prev) => [p, ...prev]); if (p?.author?.user_id === user?.user_id) setPosted(true); }} />
          {!space && <SuggestedConnections />}
          {loading ? (
            <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>
          ) : posts.length === 0 ? (
            scope === "subscribed" && !space ? (
              <div className="text-center py-16 border border-dashed border-border rounded-xl text-muted-foreground" data-testid="feed-empty">
                You're not subscribed to any spaces yet — subscribe to a space to fill this wall.
              </div>
            ) : (
              <div className="border border-gold/30 bg-secondary/30 rounded-xl p-8 sm:p-10 text-center fade-up" data-testid="feed-empty">
                <div className="mx-auto w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center mb-4">
                  <Sparkles size={22} className="text-gold" />
                </div>
                <h3 className="font-serif text-2xl font-bold">
                  {space ? `Be the first voice in ${space.name}` : "It's quiet in here — for now"}
                </h3>
                <p className="text-sm text-muted-foreground mt-2 mb-6 max-w-md mx-auto leading-relaxed">
                  {space
                    ? "Every great space starts with one honest post. Kick it off and set the tone for everyone who joins after you."
                    : "You're early — that's the best time to be here. Share the first post and shape what this community becomes."}
                </p>
                <button onClick={startFirstPost} data-testid="feed-empty-start-post"
                  className="inline-flex items-center gap-2 bg-gold text-accent-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors">
                  <Send size={15} /> Write the first post
                </button>
              </div>
            )
          ) : (
            <div className="space-y-5">
              {posts.map((p) => (
                <PostCard key={p.post_id} post={p} onChange={load}
                  onDelete={(id) => setPosts((prev) => prev.filter((x) => x.post_id !== id))} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
